import React, {createContext, useCallback, useContext, useMemo, useState} from 'react';
import {appMessages} from './messages';
import {APP_LOCALE_STORAGE_KEY, AppLocale, DEFAULT_APP_LOCALE, I18nContextValue} from './i18n.types';

const I18nContext = createContext<I18nContextValue | null>(null);

const resolveInitialLocale = (): AppLocale => {
    if (typeof window === 'undefined') {
        return DEFAULT_APP_LOCALE;
    }

    const storedLocale = window.localStorage.getItem(APP_LOCALE_STORAGE_KEY) as AppLocale | null;
    if (storedLocale && appMessages[storedLocale]) {
        return storedLocale;
    }

    return DEFAULT_APP_LOCALE;
};

interface I18nProviderProps {
    children: React.ReactNode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({children}) => {
    const [locale, setLocaleState] = useState<AppLocale>(resolveInitialLocale);

    const setLocale = useCallback((nextLocale: AppLocale) => {
        setLocaleState(nextLocale);
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(APP_LOCALE_STORAGE_KEY, nextLocale);
        }
    }, []);

    const t = useCallback((key: string) => {
        const localeMessages = appMessages[locale];
        const defaultMessages = appMessages[DEFAULT_APP_LOCALE];

        return localeMessages[key] ?? defaultMessages[key] ?? key;
    }, [locale]);

    const value = useMemo<I18nContextValue>(() => ({
        locale,
        setLocale,
        t
    }), [locale, setLocale, t]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18nContext = (): I18nContextValue => {
    const context = useContext(I18nContext);

    if (!context) {
        throw new Error('useI18nContext must be used within I18nProvider');
    }

    return context;
};
